import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import PWAInstallPrompt from '../components/PWAInstallPrompt';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_groom-calendar/artifacts/u8mpsttn_1000064271.jpg';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!pin) {
      toast.error('Please enter your PIN');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        { pin },
        { withCredentials: true }
      );
      
      toast.success('Login successful!');
      navigate('/admin/dashboard', { state: { user: response.data } });
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid PIN');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#F8F9FA' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-3xl shadow-lg p-8 md:p-12">
          <div className="text-center mb-8">
            <div className="mb-6">
              <img 
                src={LOGO_URL} 
                alt="Fur & Flair Logo" 
                className="w-64 mx-auto"
              />
            </div>
            <p className="text-lg" style={{ color: '#6B7280' }}>Admin Portal</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <Label htmlFor="pin" className="text-base font-medium mb-2 block" style={{ color: '#2F5F5F' }}>
                Enter PIN
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#6B7280' }} />
                <Input
                  id="pin"
                  data-testid="pin-input"
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                  required
                  maxLength={6}
                  className="pl-12 rounded-2xl border-2 py-6 text-lg text-center tracking-widest"
                  style={{ borderColor: '#D1E5E3', backgroundColor: '#F8F9FA', letterSpacing: '0.5em' }}
                  placeholder="------"
                />
              </div>
            </div>
            
            <button
              type="submit"
              data-testid="login-button"
              disabled={loading || pin.length < 6}
              className="w-full rounded-full px-8 py-4 font-bold text-lg transition-all active:scale-95 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#5AC5C1', color: 'white' }}
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
          
          <div className="mt-8 text-center text-sm" style={{ color: '#6B7280' }}>
            <p className="mb-4">Admin access only</p>
          </div>
        </div>
        
        <div className="mt-8 text-center">
          <a
            href="/"
            className="text-base font-medium hover:underline"
            style={{ color: '#5AC5C1' }}
          >
            ← Back to booking page
          </a>
        </div>
      </motion.div>
      
      <PWAInstallPrompt />
    </div>
  );
}
